import React, { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import './DataTable.css'

/**
 * Reusable DataTable Component
 *
 * A highly customizable, feature-rich data table component with:
 * - Column sorting (ascending/descending)
 * - Row action icon buttons
 * - Responsive design
 * - Empty state handling
 * - Loading state
 * - Customizable columns
 * - Flexible rendering
 *
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions
 *   - {string} key - Unique column identifier
 *   - {string} label - Column header label
 *   - {boolean} sortable - Whether column is sortable
 *   - {Function} render - Custom cell renderer (row, value) => ReactNode
 *   - {string} className - Additional CSS classes for column
 *   - {string} align - Text alignment (left, center, right)
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.actions - Action icon buttons
 *   - {string} label - Action label (for tooltip/aria-label)
 *   - {Function} onClick - Action handler (row) => void
 *   - {ReactNode} icon - Icon component
 *   - {string} variant - Action variant (default, danger, primary, success)
 *   - {Function} show - Conditional visibility (row) => boolean
 * @param {boolean} props.loading - Loading state
 * @param {string} props.emptyMessage - Message when no data
 * @param {Function} props.onRowClick - Row click handler
 * @param {string} props.className - Additional CSS classes
 */
const DataTable = ({
	columns = [],
	data = [],
	actions = [],
	loading = false,
	emptyMessage = 'No data available',
	onRowClick,
	className = '',
}) => {
	const [sortConfig, setSortConfig] = useState({ key: null, direction: null })

	/**
	 * Handle column sort
	 */
	const handleSort = (columnKey) => {
		const column = columns.find((col) => col.key === columnKey)
		if (!column?.sortable) return

		let direction = 'asc'
		if (sortConfig.key === columnKey) {
			if (sortConfig.direction === 'asc') {
				direction = 'desc'
			} else if (sortConfig.direction === 'desc') {
				direction = null
			}
		}

		setSortConfig({ key: columnKey, direction })
	}

	/**
	 * Sort data based on current sort configuration
	 */
	const sortedData = useMemo(() => {
		if (!sortConfig.key || !sortConfig.direction) {
			return data
		}

		return [...data].sort((a, b) => {
			const aValue = a[sortConfig.key]
			const bValue = b[sortConfig.key]

			// Handle null/undefined
			if (aValue == null) return 1
			if (bValue == null) return -1

			// String comparison
			if (typeof aValue === 'string') {
				return sortConfig.direction === 'asc'
					? aValue.localeCompare(bValue)
					: bValue.localeCompare(aValue)
			}

			// Numeric comparison
			return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
		})
	}, [data, sortConfig])

	/**
	 * Render sort icon for column header
	 */
	const renderSortIcon = (columnKey) => {
		if (sortConfig.key !== columnKey) {
			return <ChevronsUpDown size={16} className="data-table__sort-icon--inactive" />
		}
		return sortConfig.direction === 'asc' ? (
			<ChevronUp size={16} className="data-table__sort-icon--active" />
		) : (
			<ChevronDown size={16} className="data-table__sort-icon--active" />
		)
	}

	/**
	 * Handle action button click
	 */
	const handleActionClick = (action, row, event) => {
		event.stopPropagation()
		action.onClick(row)
	}

	/**
	 * Get visible actions for a row
	 */
	const getVisibleActions = (row) => {
		return actions.filter((action) => {
			return !action.show || action.show(row)
		})
	}

	const tableClasses = ['data-table', className].filter(Boolean).join(' ')

	return (
		<div className={tableClasses}>
			<div className="data-table__wrapper">
				<table className="data-table__table">
					<thead className="data-table__header">
						<tr>
							{columns.map((column) => (
								<th
									key={column.key}
									className={`data-table__header-cell ${
										column.sortable ? 'data-table__header-cell--sortable' : ''
									} ${column.align ? `data-table__header-cell--${column.align}` : ''} ${
										column.className || ''
									}`}
									onClick={() => column.sortable && handleSort(column.key)}
								>
									<div className="data-table__header-content">
										<span>{column.label}</span>
										{column.sortable && renderSortIcon(column.key)}
									</div>
								</th>
							))}
							{actions.length > 0 && (
								<th className="data-table__header-cell data-table__header-cell--actions">
									Actions
								</th>
							)}
						</tr>
					</thead>
					<tbody className="data-table__body">
						{loading ? (
							<tr>
								<td
									colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
									className="data-table__loading"
								>
									<div className="data-table__loading-content">
										<div className="loading-spinner"></div>
										<span>Loading...</span>
									</div>
								</td>
							</tr>
						) : sortedData.length === 0 ? (
							<tr>
								<td
									colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
									className="data-table__empty"
								>
									{emptyMessage}
								</td>
							</tr>
						) : (
							sortedData.map((row, rowIndex) => {
								const visibleActions = getVisibleActions(row)
								return (
									<tr
										key={row.id || rowIndex}
										className={`data-table__row ${
											onRowClick ? 'data-table__row--clickable' : ''
										}`}
										onClick={() => onRowClick && onRowClick(row)}
									>
										{columns.map((column) => {
											const value = row[column.key]
											return (
												<td
													key={column.key}
													className={`data-table__cell ${
														column.align ? `data-table__cell--${column.align}` : ''
													} ${column.className || ''}`}
												>
													{column.render ? column.render(row, value) : value || '-'}
												</td>
											)
										})}
										{actions.length > 0 && (
											<td className="data-table__cell data-table__cell--actions">
												<div className="data-table__actions-buttons">
													{visibleActions.map((action, actionIndex) => {
														// Support dynamic label, icon, and variant (can be functions)
														const label = typeof action.label === 'function' ? action.label(row) : action.label
														const icon = typeof action.icon === 'function' ? action.icon(row) : action.icon
														const variant = typeof action.variant === 'function' ? action.variant(row) : (action.variant || 'default')

														return (
															<button
																key={actionIndex}
																className={`data-table__action-button data-table__action-button--${variant}`}
																onClick={(e) => handleActionClick(action, row, e)}
																aria-label={label}
																title={label}
															>
																{icon}
															</button>
														)
													})}
												</div>
											</td>
										)}
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default DataTable
